// src/hooks/useDashboard.ts
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DashboardService } from '../admin/pages/dashboard/dashboardService';
import { ReservationService } from '../admin/pages/reservation/reservationService';
import { TradeInService } from '../admin/pages/tradein/tradeInService';

export const DASHBOARD_KEYS = {
  all:      ['dashboard'] as const,
  data:     () => ['dashboard', 'data'] as const,
  pipeline: () => ['dashboard', 'pipeline'] as const,
};

// 5 minutes — dashboard is expensive (full collection reads + warehouse inventory),
// so we hold the cache longer than paginated admin lists.
const DASHBOARD_STALE_TIME = 1000 * 60 * 5;

export const useDashboardData = () => {
  return useQuery({
    queryKey: DASHBOARD_KEYS.data(),
    queryFn: () => DashboardService.getDashboardData(),
    staleTime: DASHBOARD_STALE_TIME,
    // Keep previous data visible while a background refetch runs so the
    // dashboard never shows a blank/loading state on re-visits.
    placeholderData: (prev: any) => prev,
  });
};

export interface PipelineStats {
  newInquiries: number;
  reserved: number;
  pendingTradeIns: number;
}

// Sales pipeline snapshot sourced from live `reservations` / `tradeInSubmissions`
// collections — unlike the legacy `orders`/`warehouse_inventory`-derived
// dashboard stats, these collections are actually written to by the app today.
export const usePipelineStats = () => {
  return useQuery<PipelineStats>({
    queryKey: DASHBOARD_KEYS.pipeline(),
    queryFn: async () => {
      const [reservationStats, tradeIns] = await Promise.all([
        ReservationService.getStats(),
        TradeInService.getAll(),
      ]);
      const pendingTradeIns = tradeIns.filter(
        t => t.status === 'New Submission' || t.status === 'Under Review'
      ).length;
      return {
        newInquiries: reservationStats.newInquiries,
        reserved: reservationStats.reserved,
        pendingTradeIns,
      };
    },
    staleTime: DASHBOARD_STALE_TIME,
    placeholderData: (prev: any) => prev,
  });
};

export const useRefreshDashboard = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: DASHBOARD_KEYS.all });
};